/**
 * PDF Export Service
 *
 * Replaces the deprecated ng-pdf-make package with direct pdfmake usage.
 * Provides a simplified API for creating PDF documents with tables.
 *
 * @example
 * ```typescript
 * import { PdfExportService, PdfTableConfig } from './pdf-export.service';
 *
 * const tableConfig: PdfTableConfig = {
 *   headers: ['Name', 'Email', 'Total'],
 *   rows: [
 *     ['John Doe', 'john@example.com', '£100'],
 *     ['Jane Doe', 'jane@example.com', '£200']
 *   ],
 *   widths: ['*', '*', 'auto']
 * };
 *
 * PdfExportService.downloadTable('Orders Report', tableConfig, 'orders-report');
 * ```
 */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';

// Initialize pdfMake with fonts
pdfMake['vfs'] = pdfFonts['vfs'];

export interface PdfTableConfig {
  /** Column headers */
  headers: string[];
  /** Data rows - array of arrays */
  rows: (string | number | null | undefined)[][];
  /** Column widths - can be 'auto', '*', or numbers */
  widths?: (string | number)[];
}

export interface PdfStyleOptions {
  /** Font size for header */
  headerFontSize?: number;
  /** Font size for table */
  tableFontSize?: number;
  /** Header alignment */
  headerAlignment?: 'left' | 'center' | 'right';
}

export class PdfExportService {
  private static defaultStyles: PdfStyleOptions = {
    headerFontSize: 18,
    tableFontSize: 10,
    headerAlignment: 'left',
  };

  /**
   * Download a PDF with a title and table
   * @param title Document title
   * @param tableConfig Table configuration
   * @param filename Download filename (without .pdf)
   * @param styles Optional style overrides
   */
  static downloadTable(
    title: string,
    tableConfig: PdfTableConfig,
    filename: string,
    styles: PdfStyleOptions = {},
  ): void {
    const mergedStyles = { ...this.defaultStyles, ...styles };
    const docDefinition = this.createTableDocument(
      title,
      tableConfig,
      mergedStyles,
    );

    pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
  }

  /**
   * Open PDF in new browser tab
   */
  static openTable(
    title: string,
    tableConfig: PdfTableConfig,
    styles: PdfStyleOptions = {},
  ): void {
    const mergedStyles = { ...this.defaultStyles, ...styles };
    const docDefinition = this.createTableDocument(
      title,
      tableConfig,
      mergedStyles,
    );

    pdfMake.createPdf(docDefinition).open();
  }

  /**
   * Create document definition for pdfmake
   */
  private static createTableDocument(
    title: string,
    tableConfig: PdfTableConfig,
    styles: PdfStyleOptions,
  ): TDocumentDefinitions {
    const { headers, rows, widths } = tableConfig;

    // Create header row with bold styling
    const headerRow = headers.map((header) => ({
      text: header,
      bold: true,
      fillColor: '#f5f5f5',
    }));

    // Create data rows, converting null/undefined to empty string
    const dataRows = rows.map((row) =>
      row.map((cell) =>
        cell === null || cell === undefined ? '' : String(cell),
      ),
    );

    // Combine header and data rows
    const tableBody = [headerRow, ...dataRows];

    // Calculate widths - default to '*' (equal distribution)
    const columnWidths = widths || headers.map(() => '*');

    return {
      content: [
        {
          text: title,
          style: 'header',
          margin: [0, 0, 0, 20],
        },
        {
          table: {
            headerRows: 1,
            widths: columnWidths,
            body: tableBody,
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#dddddd',
            vLineColor: () => '#dddddd',
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
          },
        },
      ],
      styles: {
        header: {
          fontSize: styles.headerFontSize,
          bold: true,
          alignment: styles.headerAlignment,
        },
      },
      defaultStyle: {
        fontSize: styles.tableFontSize,
      },
      pageOrientation: 'landscape',
      pageMargins: [40, 60, 40, 60],
    } as TDocumentDefinitions;
  }

  /**
   * Create a simple PDF with just text content
   * @param title Document title
   * @param content Text content
   * @param filename Download filename
   */
  static downloadText(title: string, content: string, filename: string): void {
    const docDefinition: TDocumentDefinitions = {
      content: [
        { text: title, style: 'header', margin: [0, 0, 0, 20] },
        { text: content },
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
        },
      },
    };

    pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
  }
}
